// Personal dashboard — KPI cards, chat rooms, member directory, notices.
import { useEffect, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../shared/api/client";
import { useAuth } from "../../shared/stores/auth";
import { PageHeader } from "../../shared/components/PageHeader";
import { Loading } from "../../shared/components/Empty";
import { Pill } from "../../shared/components/Pill";
import { I } from "../../shared/Icon";
import { toast } from "../../shared/stores/toasts";

// ─── Types ────────────────────────────────────────────────────────────────────

type Summary = {
  todos: { total: number; done: number; pending: number };
  mail: { unread: number };
  approvals: { waiting_me: number; in_progress: number; rejected: number };
  events: { today: number };
  attendance: { checked_in: boolean; check_in_at: string | null; check_out_at: string | null; work_minutes_today: number };
  recent_notices: { id: number; title: string; category: string; created_at: string }[];
};

type RoomRead = {
  id: number;
  type: "DIRECT" | "GROUP" | "DEPARTMENT" | "PROJECT" | "OPEN";
  name: string | null;
  member_count: number;
  created_at: string;
  is_archived: boolean;
};

type MemberItem = {
  id: number;
  name: string;
  department_name: string | null;
  position_name: string | null;
  status: string;
  profile_image: string | null;
};

// ─── Cache helpers ─────────────────────────────────────────────────────────────

const CACHE_KEY = "dash:me:v1";
function readCache(uid: number | null): Summary | null {
  if (uid == null) return null;
  try {
    const raw = localStorage.getItem(`${CACHE_KEY}:${uid}`);
    return raw ? (JSON.parse(raw) as Summary) : null;
  } catch { return null; }
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function DashboardPage() {
  const uid = useAuth((s) => s.user?.id ?? null);
  const userName = useAuth((s) => s.user?.name ?? "");
  const [data, setData] = useState<Summary | null>(() => readCache(uid));
  const [rooms, setRooms] = useState<RoomRead[]>([]);
  const [members, setMembers] = useState<MemberItem[]>([]);
  const nav = useNavigate();

  // Refresh just the summary (used by attendance check-in/out to reflect changes).
  const refreshSummary = () =>
    api.get<Summary>("/dashboard/me").then((r) => {
      setData(r.data);
      if (uid != null) {
        try { localStorage.setItem(`${CACHE_KEY}:${uid}`, JSON.stringify(r.data)); } catch { /* quota */ }
      }
    }).catch(() => { /* keep stale */ });

  useEffect(() => {
    let alive = true;

    // Summary — paint cached data first, then refresh
    const cached = readCache(uid);
    if (cached) setData(cached);

    api.get<Summary>("/dashboard/me").then((r) => {
      if (!alive) return;
      setData(r.data);
      if (uid != null) {
        try { localStorage.setItem(`${CACHE_KEY}:${uid}`, JSON.stringify(r.data)); } catch { /* quota */ }
      }
    }).catch(() => { /* keep stale */ });

    // Chat rooms
    api.get<RoomRead[]>("/chat/rooms").then((r) => {
      if (!alive) return;
      setRooms(r.data.filter((rm) => !rm.is_archived).slice(0, 8));
    }).catch(() => {});

    // Members
    api.get<any>("/users", { params: { size: 12, status: "ACTIVE" } })
      .then((r) => { if (!alive) return; const b: any = r.data; setMembers(Array.isArray(b) ? b : (b?.data ?? b?.items ?? [])); })
      .catch(() => {});

    return () => { alive = false; };
  }, [uid]);

  if (!data) return <><PageHeader title="대시보드" subtitle="오늘 한눈에 보기" /><Loading /></>;

  const hour = new Date().getHours();
  const greeting = hour < 12 ? "좋은 아침입니다" : hour < 18 ? "안녕하세요" : "수고하셨습니다";

  return (
    <>
      <PageHeader
        title={`${greeting}, ${userName || "님"}`}
        subtitle="오늘의 업무 현황"
      />

      <div className="t-grid cols-3">

        {/* ── KPI row ── */}
        <KpiCard
          icon={<I.Approve size={18} />}
          label="결재 대기"
          value={data.approvals.waiting_me}
          accent="var(--m-approval)"
          onClick={() => nav("/approval?filter=waiting")}
        />
        <KpiCard
          icon={<I.Mail size={18} />}
          label="안 읽은 메일"
          value={data.mail.unread}
          accent="var(--m-collab)"
          onClick={() => nav("/mail")}
        />
        <KpiCard
          icon={<I.Check size={18} />}
          label="할 일 (미완료)"
          value={data.todos.pending}
          accent="var(--m-task)"
          onClick={() => nav("/todos")}
        />

        {/* ── Chat rooms (span-2) + Members (1 col) ── */}
        <ChatCard rooms={rooms} onNavigate={() => nav("/chat")} />
        <MembersCard members={members} onNavigate={() => nav("/directory")} />

        {/* ── Attendance (1 col) + Notices (span-2) ── */}
        <AttendanceCard attendance={data.attendance} onChanged={refreshSummary} />
        <NoticesCard notices={data.recent_notices} onClickNotice={(id) => nav(`/notice/${id}`)} />

      </div>
    </>
  );
}

// ─── KPI card ─────────────────────────────────────────────────────────────────

function KpiCard({ icon, label, value, accent, onClick }: {
  icon: ReactNode; label: string; value: number; accent: string; onClick?: () => void;
}) {
  return (
    <div className="t-card" style={{ cursor: onClick ? "pointer" : "default" }} onClick={onClick}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{
          width: 38, height: 38, borderRadius: 10, flexShrink: 0,
          background: `color-mix(in srgb, ${accent} 14%, transparent)`,
          color: accent, display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          {icon}
        </div>
        <div>
          <div style={{ fontSize: 11.5, color: "var(--text-3)" }}>{label}</div>
          <div className="num" style={{ fontSize: 26, fontWeight: 700, letterSpacing: "-0.02em" }}>{value}</div>
        </div>
      </div>
    </div>
  );
}

// ─── Chat rooms card ──────────────────────────────────────────────────────────

const ROOM_TYPE_LABEL: Record<string, string> = {
  DIRECT: "DM", GROUP: "그룹", DEPARTMENT: "부서", PROJECT: "프로젝트", OPEN: "공개",
};

function ChatCard({ rooms, onNavigate }: { rooms: RoomRead[]; onNavigate: () => void }) {
  return (
    <div className="t-card span-2">
      <div className="t-card-h" style={{ marginBottom: 8, justifyContent: "space-between" }}>
        <h3>
          <I.Msg size={16} style={{ color: "var(--m-collab)" }} /> 채팅 현황
          {rooms.length > 0 && (
            <span style={{ fontSize: 11, fontWeight: 500, color: "var(--text-3)", background: "var(--panel)", borderRadius: 20, padding: "1px 7px" }}>
              {rooms.length}개 채널
            </span>
          )}
        </h3>
        <button className="t-btn t-btn-outline t-btn-sm" onClick={onNavigate}>채팅 열기</button>
      </div>

      {rooms.length === 0 ? (
        <div className="t-emptyspace">참여 중인 채널이 없습니다</div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
          {rooms.map((rm) => (
            <RoomItem key={rm.id} room={rm} onClick={onNavigate} />
          ))}
        </div>
      )}
    </div>
  );
}

function RoomItem({ room: rm, onClick }: { room: RoomRead; onClick: () => void }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "flex", alignItems: "center", gap: 8,
        padding: "8px 10px", borderRadius: 8, cursor: "pointer",
        background: hovered ? "var(--hover)" : "var(--panel)",
        transition: "background 0.12s",
      }}
    >
      <div style={{
        width: 32, height: 32, borderRadius: 8, flexShrink: 0,
        background: "color-mix(in srgb, var(--m-collab) 18%, transparent)",
        color: "var(--m-collab)", display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 13, fontWeight: 700,
      }}>
        {rm.type === "DIRECT" ? <I.Users size={14} /> : (rm.name?.slice(0, 1) ?? "#")}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {rm.name ?? "다이렉트 메시지"}
        </div>
        <div style={{ fontSize: 11, color: "var(--text-3)", display: "flex", alignItems: "center", gap: 4, marginTop: 1 }}>
          <span>{ROOM_TYPE_LABEL[rm.type] ?? rm.type}</span>
          <span>·</span>
          <span>{rm.member_count}명</span>
        </div>
      </div>
    </div>
  );
}

// ─── Members card ─────────────────────────────────────────────────────────────

const AVATAR_COLORS = [
  "#6E6FCF", "#0891B2", "#16A34A", "#D97706", "#DB2777", "#7C3AED", "#0F766E", "#DC2626",
];
function avatarColor(name: string) {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) & 0xffffffff;
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}

function MembersCard({ members, onNavigate }: { members: MemberItem[]; onNavigate: () => void }) {
  return (
    <div className="t-card">
      <div className="t-card-h" style={{ marginBottom: 8, justifyContent: "space-between" }}>
        <h3>
          <I.Users size={16} style={{ color: "var(--m-hrm)" }} /> 임직원
        </h3>
        <button className="t-btn t-btn-outline t-btn-sm" onClick={onNavigate}>전체 보기</button>
      </div>

      {members.length === 0 ? (
        <div className="t-emptyspace">멤버 정보를 불러오는 중…</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {members.slice(0, 8).map((m) => (
            <div key={m.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0" }}>
              <div style={{
                width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
                background: avatarColor(m.name), color: "#fff",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 11, fontWeight: 700,
              }}>
                {m.name.slice(0, 2)}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12.5, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {m.name}
                </div>
                {(m.department_name || m.position_name) && (
                  <div style={{ fontSize: 10.5, color: "var(--text-3)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {[m.department_name, m.position_name].filter(Boolean).join(" · ")}
                  </div>
                )}
              </div>
              <div style={{
                width: 7, height: 7, borderRadius: "50%", flexShrink: 0,
                background: m.status === "ACTIVE" ? "var(--ok)" : "var(--border)",
              }} />
            </div>
          ))}
          {members.length > 8 && (
            <button
              className="t-btn t-btn-sm"
              style={{ marginTop: 4, width: "100%", fontSize: 11, color: "var(--text-3)" }}
              onClick={onNavigate}
            >
              +{members.length - 8}명 더 보기
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Attendance card ───────────────────────────────────────────────────────────

function AttendanceCard({ attendance, onChanged }: { attendance: Summary["attendance"]; onChanged: () => void | Promise<void> }) {
  const [busy, setBusy] = useState(false);
  const checkInTime = attendance.check_in_at
    ? new Date(attendance.check_in_at).toLocaleTimeString("ko-KR", { hour: "2-digit", minute: "2-digit" })
    : null;
  const hours = Math.floor(attendance.work_minutes_today / 60);
  const mins = attendance.work_minutes_today % 60;
  const checkedOut = !!attendance.check_out_at;

  const checkIn = async () => {
    setBusy(true);
    try {
      await api.post("/attendance/check-in", { method: "WEB" });
      toast.success("출근 체크 완료");
      await onChanged();
    } catch (e: any) {
      toast.error("출근 체크 실패", e?.response?.data?.error?.message ?? "");
    } finally { setBusy(false); }
  };

  const checkOut = async () => {
    setBusy(true);
    try {
      await api.post("/attendance/check-out");
      toast.success("퇴근 체크 완료");
      await onChanged();
    } catch (e: any) {
      toast.error("퇴근 체크 실패", e?.response?.data?.error?.message ?? "");
    } finally { setBusy(false); }
  };

  const undoCheckOut = async () => {
    if (!confirm("퇴근 체크를 취소하고 다시 근무 중 상태로 되돌릴까요?")) return;
    setBusy(true);
    try {
      await api.post("/attendance/check-out/undo");
      toast.success("퇴근 정정 완료", "다시 근무 중 상태입니다");
      await onChanged();
    } catch (e: any) {
      toast.error("퇴근 정정 실패", e?.response?.data?.error?.message ?? "");
    } finally { setBusy(false); }
  };

  return (
    <div className="t-card">
      <div className="t-card-h"><h3>오늘 근태</h3></div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 12, marginBottom: 4 }}>
        <div className="num" style={{ fontSize: 28, fontWeight: 700 }}>
          {checkInTime ?? "—"}
        </div>
        <Pill kind={checkedOut ? "info" : attendance.checked_in ? "ok" : "muted"}>
          {checkedOut ? "퇴근" : attendance.checked_in ? "출근" : "미출근"}
        </Pill>
      </div>
      <div style={{ fontSize: 12, color: "var(--text-3)", marginBottom: 14 }}>
        근무 시간: <strong className="num">{hours}h {mins}m</strong>
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <button
          className="t-btn t-btn-primary t-btn-sm"
          style={{ flex: 1 }}
          disabled={busy || attendance.checked_in}
          onClick={checkIn}
        >
          출근 체크
        </button>
        {checkedOut ? (
          <button
            className="t-btn t-btn-outline t-btn-sm"
            style={{ flex: 1 }}
            disabled={busy}
            onClick={undoCheckOut}
            title="잘못 누른 퇴근을 취소합니다"
          >
            퇴근 정정
          </button>
        ) : (
          <button
            className="t-btn t-btn-outline t-btn-sm"
            style={{ flex: 1 }}
            disabled={busy || !attendance.checked_in}
            onClick={checkOut}
          >
            퇴근 체크
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Notices card ─────────────────────────────────────────────────────────────

function NoticesCard({
  notices, onClickNotice,
}: {
  notices: Summary["recent_notices"];
  onClickNotice: (id: number) => void;
}) {
  return (
    <div className="t-card span-2">
      <div className="t-card-h" style={{ marginBottom: 4 }}>
        <h3>
          <I.Bell size={16} style={{ color: "var(--m-system)" }} /> 최근 공지
        </h3>
      </div>
      <div className="t-list">
        {notices.length === 0 && <div className="t-emptyspace">공지가 없습니다</div>}
        {notices.map((n) => (
          <div key={n.id} className="t-row clickable" onClick={() => onClickNotice(n.id)}>
            <Pill kind="info">{n.category}</Pill>
            <span className="title" style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {n.title}
            </span>
            <span className="meta">{new Date(n.created_at).toLocaleDateString("ko-KR")}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
